import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMutation } from '@tanstack/react-query'
import React from 'react'

import DataTable from '@/components/datatable/DataTable'
import FilePicker from '@/components/misc/FilePicker'
import { Button } from '@/components/ui/button'
import { Heading } from '@/components/ui/heading'
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import useNotyf from '@/hooks/useNotyf'
import { useShowGenericServerError } from '@/hooks/useShowGenericServerError'
import {
  LONG_MESSAGE_DURATION,
  MEDIA_OBJECT_CONTEXT,
  PDF_DIMENSIONS,
} from '@/lib/constants'
import {
  faArrowDown,
  faArrowUp,
  faTrashAlt,
} from '@/lib/fontAwesomePro/regular'
import { buildDebugTooltip } from '@/lib/helpers/helperFunctions'
import constraintViolationService from '@/lib/services/constraintViolationService'
import mediaObjectService from '@/lib/services/mediaObjectService'
import { cn } from '@/lib/utils'

export type AdditionalClientConsentFile = {
  '@id': IRI
  originalFileName: string
  fileUrl: string
  token: string
}

type Props = React.ComponentProps<'div'> & {
  consentFiles: AdditionalClientConsentFile[]
  onAddConsentFile: (file: AdditionalClientConsentFile) => void
  onRemoveConsentFile: (consentFileToken: string) => void
  onMoveConsentFileUp: (consentFileToken: string) => void
  onMoveConsentFileDown: (consentFileToken: string) => void
}

const AdditionalConsentFiles: React.FC<Props> = ({
  className,
  style,
  consentFiles,
  onAddConsentFile,
  onRemoveConsentFile,
  onMoveConsentFileUp,
  onMoveConsentFileDown,
  ...props
}) => {
  const showGenericServerError = useShowGenericServerError()
  const notyf = useNotyf()

  const uploadMediaObjectMutation = useMutation({
    mutationFn: (file: File) =>
      mediaObjectService.uploadMediaObject({
        file: file,
        context: MEDIA_OBJECT_CONTEXT.CLIENT_CONSENT_FILE,
      }),
    onSuccess: async (mediaObject) => {
      if (typeof onAddConsentFile !== 'function') {
        throw new Error('Expected onAddConsentFile to be a function')
      }

      onAddConsentFile(mediaObject)
    },
    onError: (error) => {
      const fileErrorMessage =
        constraintViolationService.getFileViolationMessage(error)

      if (fileErrorMessage) {
        notyf.open({
          type: 'error',
          message: fileErrorMessage,
          duration: LONG_MESSAGE_DURATION,
        })

        return
      }

      showGenericServerError()
    },
  })

  const numConsentFiles = consentFiles.length

  const hasMultipleFiles = numConsentFiles > 1

  return (
    <div
      className={cn('mx-auto', className)}
      style={{
        maxWidth: PDF_DIMENSIONS.A4.portraitWidth,
        borderTop: '1px solid #ccc',
        paddingTop: '30px',
        marginTop: '20px',
        ...style,
      }}
      {...props}
    >
      <>
        <div className="mb-2 flex items-center gap-2">
          <Heading size={3} className="mb-0 text-black">
            Additional PDFs {numConsentFiles > 0 && `(${numConsentFiles})`}
          </Heading>
        </div>

        <p className="mb-4">
          You can add additional PDFs that you want appended to the eSignature
          letter PDF. Any PDF pages you add will be merged with the the
          eSignature letter PDF page to form a single PDF file.
        </p>
      </>

      {numConsentFiles > 0 && (
        <DataTable
          tableContainerProps={{
            className: 'mb-6',
          }}
          tableProps={{
            className: 'table-fixed w-[430px]',
          }}
        >
          <colgroup>
            <col style={{ width: '300px' }} />
            <col style={{ width: '120px' }} />
          </colgroup>

          <TableHeader>
            <TableRow>
              <TableHead>File</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {consentFiles.map((file, index) => {
              const isFirstFile = index === 0
              const isLastFile = index === consentFiles.length - 1
              const canMoveUp = !isFirstFile
              const canMoveDown = !isLastFile

              return (
                <TableRow key={file.fileUrl}>
                  <TableCell>
                    <a
                      title={buildDebugTooltip({
                        token: file.token,
                      })}
                      href={file.fileUrl}
                      download
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        display: 'block',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        maxWidth: '300px',
                        overflowX: 'hidden',
                      }}
                    >
                      {file.originalFileName}
                    </a>
                  </TableCell>

                  <TableCell>
                    <div className="flex flex-wrap gap-2">
                      <div
                        className={cn('flex items-center gap-2', {
                          'w-full': !hasMultipleFiles,
                        })}
                      >
                        {hasMultipleFiles && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                disabled={!canMoveUp}
                                className={cn({
                                  'opacity-25-25': !canMoveUp,
                                })}
                                onClick={() => onMoveConsentFileUp(file.token)}
                              >
                                <FontAwesomeIcon icon={faArrowUp} />
                              </Button>
                            </TooltipTrigger>

                            <TooltipContent>Move up</TooltipContent>
                          </Tooltip>
                        )}

                        {hasMultipleFiles && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                disabled={!canMoveDown}
                                className={cn({
                                  'opacity-25': !canMoveDown,
                                })}
                                onClick={() =>
                                  onMoveConsentFileDown(file.token)
                                }
                              >
                                <FontAwesomeIcon icon={faArrowDown} />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Move up</TooltipContent>
                          </Tooltip>
                        )}

                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline-destructive"
                              className={cn({
                                'w-full': !hasMultipleFiles,
                              })}
                              size="sm"
                              onClick={() => onRemoveConsentFile(file.token)}
                            >
                              {hasMultipleFiles ? (
                                <FontAwesomeIcon icon={faTrashAlt} />
                              ) : (
                                <>Remove</>
                              )}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Remove</TooltipContent>
                        </Tooltip>
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </DataTable>
      )}

      <FilePicker
        accept="application/pdf"
        modalTitle="Add PDF"
        maxFileSizeMb={20}
        maxFiles={1}
        onConfirm={async (files) => {
          const file = files[0]

          if (!file) {
            throw new Error('Expected file to be set')
          }

          return uploadMediaObjectMutation.mutateAsync(file)
        }}
        buttonLabel="Add PDF"
        buttonVariant="primary"
        buttonSize="lg"
        confirmButtonText="Add PDF"
        isProcessing={uploadMediaObjectMutation.isPending}
      />
    </div>
  )
}

export default AdditionalConsentFiles
